/**
 * RecommendedVideos widget — AC1–4, 7–8.
 * Shows horizontal scroll strip of up to 5 recommended video cards.
 * Hidden on mobile (AC8), hidden entirely when result is empty (AC7).
 * Fetches from GET /api/v1/knowledge/videos/recommended (implemented in v9-3-1).
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, CheckCircle } from 'lucide-react';
import VideoCardSkeleton from './VideoCardSkeleton';
import ThumbnailImg from './ThumbnailImg';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';

interface Video {
  id: string;
  title: string;
  difficulty?: string;
  duration_sec?: number;
  thumbnail_key?: string;
  topic_tags: string[];
}

interface ProgressData {
  watched_seconds: number;
  completed: boolean;
}

async function fetchRecommended(): Promise<Video[]> {
  const token = localStorage.getItem('token') ?? '';
  const res = await fetch(`${API_BASE}/api/v1/knowledge/videos/recommended`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : (data.items ?? []);
}

async function fetchProgressBatch(videoIds: string[]): Promise<Record<string, ProgressData>> {
  if (!videoIds.length) return {};
  const token = localStorage.getItem('token') ?? '';
  const params = new URLSearchParams(videoIds.map((id) => ['video_ids', id]));
  const res = await fetch(`${API_BASE}/api/v1/knowledge/videos/progress-batch?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return {};
  return res.json();
}

const formatDuration = (secs?: number) => {
  if (!secs) return '';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const difficultyColour: Record<string, string> = {
  starter: 'bg-green-500/20 text-green-400',
  intermediate: 'bg-amber-500/20 text-amber-400',
  advanced: 'bg-red-500/20 text-red-400',
};

interface VideoCardCompactProps {
  video: Video;
  progress?: ProgressData;
}

export const VideoCardCompact: React.FC<VideoCardCompactProps> = ({ video, progress }) => {
  const navigate = useNavigate();
  const pct =
    video.duration_sec && progress && progress.watched_seconds > 0
      ? Math.min(100, Math.round((progress.watched_seconds / video.duration_sec) * 100))
      : 0;

  return (
    <button
      onClick={() => navigate(`/knowledge/${video.id}`)}
      className="flex-shrink-0 w-44 rounded-xl bg-slate-800/70 border border-slate-700/40 hover:border-teal-500/40 transition-all hover:-translate-y-0.5 text-left group"
    >
      {/* Thumbnail */}
      <div className="h-24 bg-slate-900 rounded-t-xl overflow-hidden relative flex items-center justify-center">
        {video.thumbnail_key ? (
          <ThumbnailImg
            videoId={video.id}
            alt={video.title}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
          />
        ) : (
          <BookOpen size={20} className="text-slate-600" />
        )}
        {progress?.completed && (
          <span className="absolute top-1.5 left-1.5 flex items-center gap-0.5 bg-green-600/90 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium">
            <CheckCircle size={8} />
            Done
          </span>
        )}
        {video.duration_sec && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] px-1 py-0.5 rounded">
            {formatDuration(video.duration_sec)}
          </span>
        )}
      </div>
      {/* Info */}
      <div className="p-2.5">
        <p className="text-xs font-medium text-white line-clamp-2 leading-snug mb-1">{video.title}</p>
        {video.difficulty && (
          <span className={`inline-flex text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${difficultyColour[video.difficulty] ?? 'bg-slate-500/20 text-slate-400'}`}>
            {video.difficulty}
          </span>
        )}
        {/* Progress bar */}
        {pct > 0 && !progress?.completed && (
          <div className="mt-1.5 h-1 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </button>
  );
};

export default function RecommendedVideos() {
  const { data: videos, isLoading } = useQuery<Video[]>({
    queryKey: ['recommended-videos'],
    queryFn: fetchRecommended,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const videoIds = videos?.map((v) => v.id) ?? [];

  const { data: progressMap = {} } = useQuery<Record<string, ProgressData>>({
    queryKey: ['knowledge-progress-batch', videoIds],
    queryFn: () => fetchProgressBatch(videoIds),
    enabled: videoIds.length > 0,
    staleTime: 60_000,
  });

  // AC7: hide entirely when result is empty (not loading)
  if (!isLoading && (!videos || videos.length === 0)) return null;

  return (
    // AC8: hidden on mobile
    <div className="hidden md:block">
      <h3 className="text-sm font-semibold text-slate-400 mb-3">Recommended for You</h3>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => <VideoCardSkeleton key={i} />)
          : videos!.slice(0, 5).map((v) => (
              <VideoCardCompact key={v.id} video={v} progress={progressMap[v.id]} />
            ))}
      </div>
    </div>
  );
}
